import { useApolloClient, useMutation } from '@apollo/client';
import { jwtDecode } from 'jwt-decode';
import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer/index';
import Header from '../components/Header/header';
import { Hovercard } from '../components/Hovercard/Hovercard';
import { useHovercard } from '../components/Hovercard/useHovercard';
import UserLogin from '../components/Login/UserLogin';
import { PostPublishPrompt } from '../components/PlanC/PostPublishPrompt';
import { ProfileCompletionNudge } from '../components/PlanC/ProfileCompletionNudge';
import RequestResetPassword from '../components/RequestResetPassword/index';
import ResetPassword from '../components/ResetPassword/index';
import SearchBar from '../components/SearchBar';
import SharedWithYou from '../components/SharedWithYou';
import UserSignUp from '../components/Signup/UserSignUp';
import TrendingSection from '../components/TrendingSection';
import SimpleBackdrop from '../components/UI/SimpleBackdrop';
import TransitionAlert from '../components/UI/TransitionAlert';
import UserHydrationGate from '../components/UserHydrationGate';
import VerifyIdentity from '../components/VerifyIdentity/VerifyIdentity';
import { apiRoutes } from '../constants/api-routes.constants';
import { DRAFT } from '../constants/draft.constants';
import { PRIVATE_ROUTES } from '../constants/privateRoutes.constants';
import { PUBLIC_ROUTES } from '../constants/publicRoutes.constants';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/WebsocketContext';
import { UPDATE_SIDEBAR_ORDER } from '../graphql/resolvers/trendingQueries/updateSidebarOrder.gql';
import { UPDATE_USER_PREFERENCES_MUTATION } from '../graphql/resolvers/userMutations/updateUserPreferences.gql';
import { resolveGuardRedirectState } from '../helpers/resolveGuardRedirectState';
import { usePlanCNudgeState } from '../hooks/usePlanCNudgeState';
import { useSessionRevocationGuard } from '../hooks/useSessionRevocationGuard';
import { useVouchReceivedToast } from '../hooks/useVouchReceivedToast';
import type { GlobalItemsState } from '../interfaces/global.interface';
import type { DraftInterfaceSliceState } from '../interfaces/post.interface';
import type { UserState } from '../interfaces/user.interface';
import { draftSlice } from '../store/draft.store';
import { globalItemsSlice } from '../store/globalItems.store';
import { dispatchWebsocketEvent } from '../store/websocketMiddleware';
import '../styles/utilities.scss';
import '../globals.scss';
import './app.scss';

type Decoded = {
  user: {
    _id: string;
    username: string;
  };
  iat: number;
  exp: number;
};

export default function App(): React.ReactElement {
  const params = useParams();
  const location = useLocation();
  const ACCESS_TOKEN = localStorage.getItem('accessToken');
  const decodedId: string | Decoded = ACCESS_TOKEN
    ? jwtDecode(ACCESS_TOKEN)
    : '';
  const decodedUserId = ((decodedId as Decoded).user?._id as string) ?? '';
  const {
    isSignedIn,
    isLoading,
    loggingOutRef,
    user: contextUser,
    updateUser,
  } = useAuth();
  // TRIL-381: log this browser out if its session was revoked from another device.
  useSessionRevocationGuard();
  useVouchReceivedToast();
  const { user } = useSelector((state: UserState) => state.userStore);
  const { SET_OPEN_TRANSITION_ALERT } = globalItemsSlice.actions;
  const { openTransitionAlert } = useSelector(
    (state: GlobalItemsState) => state.globalItemsStore,
  );
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const isOnFeedRoute = /^\/user\/[^/]+\/feed\/?$/.test(location.pathname);
  const isOnProfileRoute = /^\/user\/[^/]+\/profile\/?$/.test(
    location.pathname,
  );
  // TRIL-68: the first-login interest-categories page (PRIVATE_URIS.INTERESTS)
  // gets a full-bleed layout — BOTH side columns hidden, including the left nav
  // rail (unlike the profile/analysis full-width routes which keep it) — so the
  // responsive category grid spans the full page instead of a narrow column.
  const isInterestsRoute = /^\/user\/[^/]+\/interests\/?$/.test(
    location.pathname,
  );
  // Routes that render without the right sidebar (reuses the existing
  // `is-profile-route` full-width modifier). Mirrors PRIVATE_URIS.POST_ANALYSIS.
  const isFullWidthRoute =
    isOnProfileRoute ||
    isInterestsRoute ||
    /^\/post\/[^/]+\/analysis\/?$/.test(location.pathname) ||
    /^\/reply\/[^/]+\/analysis\/?$/.test(location.pathname) ||
    /^\/post\/[^/]+\/sentiment\/?$/.test(location.pathname) ||
    /^\/post\/[^/]+\/history\/?$/.test(location.pathname) ||
    /^\/pulse\/?$/.test(location.pathname) ||
    /^\/vouch\/?$/.test(location.pathname) ||
    /^\/readme(\/[^/]+)?\/?$/.test(location.pathname) ||
    /^\/user\/[^/]+\/reputation\/?$/.test(location.pathname) ||
    /^\/user\/[^/]+\/direct-messages\/?$/.test(location.pathname);

  // TRIL-189: cross-route eviction when transitioning from /post/X to feed.
  // App.tsx is mounted across all routes, so this useRef tracks the prior
  // pathname correctly. The previous in-recommendationFeed implementation
  // was route-scoped — its useRef captured the current path at mount (the
  // feed itself), never the prior /post/X, so the eviction never fired in
  // prod. The same regex as `isOnFeedRoute` above; the `prev.startsWith(
  // '/post/')` check is safe against `/post-reply-draft/X` because the
  // trailing slash disambiguates.
  const apolloClient = useApolloClient();
  const previousPathRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = previousPathRef.current;
    const curr = location.pathname;
    const FEED_PATH_RE = /^\/user\/[^/]+\/feed\/?$/;
    if (prev?.startsWith('/post/') && FEED_PATH_RE.test(curr)) {
      apolloClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'getUserFeed',
      });
      apolloClient.cache.gc();
    }
    previousPathRef.current = curr;
  }, [location.pathname, apolloClient]);

  const planC = usePlanCNudgeState({
    context: user?.userProfile?.professionalContext ?? null,
    prefs: {
      planCNudgeDismissCount:
        user?.userPreferences?.planCNudgeDismissCount ?? 0,
      planCNudgeDismissedAt:
        user?.userPreferences?.planCNudgeDismissedAt ?? null,
      planCOptedOut: user?.userPreferences?.planCOptedOut ?? false,
    },
    userId: user?.id,
  });

  const [sectionOrder, setSectionOrder] = useState<string[]>([
    'trending',
    'shared',
  ]);
  const [updateSidebarOrder] = useMutation(UPDATE_SIDEBAR_ORDER);

  // TRIL-116 Plan C Task 21: <PostPublishPrompt /> is mounted globally because
  // the PostDraft component navigates away on publish success and unmounts.
  // PostDraft dispatches SET_LAST_PUBLISHED_TOPIC with the published post's
  // primary topic (categories[0] — Interests enum key), which surfaces here.
  const { SET_LAST_PUBLISHED_TOPIC } = draftSlice.actions;
  const lastPublishedTopic = useSelector(
    (state: { draftStore: DraftInterfaceSliceState }) =>
      state.draftStore.lastPublishedTopic,
  );
  const [updatePrefs] = useMutation(UPDATE_USER_PREFERENCES_MUTATION);

  const handlePromptDismiss = useCallback(async () => {
    const next = (user?.userPreferences?.planCPromptDismissCount ?? 0) + 1;
    dispatch(SET_LAST_PUBLISHED_TOPIC(null));
    if (user?.id) {
      await updatePrefs({
        variables: {
          input: { userId: user.id, planCPromptDismissCount: next },
        },
      }).catch(() => {});
    }
  }, [
    SET_LAST_PUBLISHED_TOPIC,
    dispatch,
    updatePrefs,
    user?.id,
    user?.userPreferences?.planCPromptDismissCount,
  ]);

  const handlePromptCta = useCallback(() => {
    dispatch(SET_LAST_PUBLISHED_TOPIC(null));
    if (user?.username) {
      navigate(`/user/${user.username}/profile#professional-background`);
    }
  }, [SET_LAST_PUBLISHED_TOPIC, dispatch, navigate, user?.username]);

  const handleSwapSections = useCallback(() => {
    const newOrder = [...sectionOrder].reverse();
    setSectionOrder(newOrder);
    if (user?.id) {
      updateSidebarOrder({
        variables: { userId: user.id, sidebarSectionOrder: newOrder },
      }).catch(() => {});
    }
  }, [sectionOrder, user?.id, updateSidebarOrder]);

  useEffect(() => {
    // Skip the sync when Redux is still at its default blank state (id === '');
    // otherwise on a refresh that synchronously rehydrates AuthContext from
    // localStorage, this effect would clobber the real user with the empty
    // Redux defaults before any user mutation populates the store.
    if (user?.id && contextUser?.id !== user.id) {
      updateUser(user);
    }
  }, [isSignedIn, user]);

  // Wire event bus to store-scoped dispatch — uses the component's
  // dispatch, not a module-level singleton
  const { onEvent } = useSocket();

  useEffect(() => {
    const unsub1 = onEvent('INTERACTION_UPDATED', (data: any) => {
      // TRIL-508: pass the current viewer so an interaction the viewer performed on another device
      // flips their own hasLiked/hasBookmarked here (not just the count).
      dispatchWebsocketEvent(dispatch, data, user?.id);
    });
    const unsub2 = onEvent('STORY_SCORE_UPDATED', (data: any) => {
      dispatchWebsocketEvent(dispatch, {
        type: 'STORY_SCORE_UPDATED',
        ...data,
      });
    });
    const unsub3 = onEvent('new_replies', (data: any) => {
      dispatchWebsocketEvent(dispatch, { type: 'new_replies', ...data });
    });
    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [onEvent, dispatch, user?.id]);

  function renderRoutes(routes: any[], privateRoute?: boolean) {
    return routes.map(
      ({ uri, Component }: { uri: string; Component: any }, index: number) => {
        // Don't decide on a private route until auth has settled — otherwise
        // a refresh on a private URL renders <Navigate to="/log-in"> on the
        // first commit, before the auth context has hydrated.
        const elementRoute = privateRoute ? (
          // TRIL-221 Phase 2: isLoading is true during the mount-time silent
          // refresh window (expired access token + valid refresh token in
          // localStorage). Rendering <SimpleBackdrop> here keeps the cold-
          // load-with-expired-access-token path looking like any other
          // loading state instead of flashing an empty <main>. This is the
          // same swap PR #158 attempted prematurely for TRIL-217 — at the
          // time the isLoading branch was unreachable. TRIL-221 makes it
          // reachable.
          isLoading ? (
            <SimpleBackdrop open={true} />
          ) : isSignedIn ? (
            <Component />
          ) : (
            // TRIL-216: preserve the original URL so UserLogin can navigate
            // back to it after authentication, instead of forcing the user
            // through the default landing page. State is in-memory via the
            // History API (not URL-injectable).
            // TRIL-231: but suppress `from` when this !isSignedIn render is an
            // explicit logout (loggingOutRef) — there is no deep-link intent,
            // so login should honor the user's landingPage preference instead.
            <Navigate
              to="/log-in"
              replace
              state={resolveGuardRedirectState({
                loggingOut: loggingOutRef.current,
                pathname: location.pathname,
              })}
            />
          )
        ) : (
          <Component />
        );
        return (
          <Route
            key={index}
            element={elementRoute}
            id={decodedUserId}
            path={uri}
          />
        );
      },
    );
  }

  const {
    LOGOUT,
    SIGNUP,
    LOGIN,
    REQUEST_RESET_PASSWORD,
    RESET_PASSWORD,
    VERIFY_IDENTITY,
  } = apiRoutes;

  const hovercardProps = useHovercard();

  const handleCloseTransitionAlert = () => {
    dispatch(SET_OPEN_TRANSITION_ALERT(false));
  };

  // TRIL-228: also treat logged-out users as on the auth path, regardless of
  // the current URL. Without this, a logged-out user cold-loading a private
  // URL (e.g. /post-reply-draft/<id>) gets the PRIVATE chrome rendered for
  // one paint cycle (header + main + sidebar with TrendingSection +
  // SharedWithYou loading skeletons) before react-router commits the
  // <Navigate to="/log-in"> redirect — surfaced after TRIL-217 cleared the
  // pre-React white-flash that previously masked this. isSignedIn is
  // hydrated synchronously from JWT/localStorage (AuthContext.tsx:172-174),
  // so this has no effect on logged-in users with a valid access token.
  const isPublicPath =
    location.pathname === SIGNUP || location.pathname === LOGIN;
  const authPath = isPublicPath || !isSignedIn;

  const allRoutes = [
    <Route key="signup" element={<UserSignUp />} path={SIGNUP} />,
    <Route key="login" element={<UserLogin />} path={LOGIN} />,
    <Route
      key="request-reset"
      element={<RequestResetPassword />}
      path={REQUEST_RESET_PASSWORD}
    />,
    <Route key="reset" element={<ResetPassword />} path={RESET_PASSWORD} />,
    <Route
      key="verify-identity"
      element={<VerifyIdentity />}
      path={VERIFY_IDENTITY}
    />,
    ...renderRoutes(PUBLIC_ROUTES, false),
    ...renderRoutes(PRIVATE_ROUTES, true),
  ];

  return (
    <div className="container-fluid APP__CONTAINER">
      <UserHydrationGate />
      <Hovercard {...hovercardProps} />
      {lastPublishedTopic && user && (
        <PostPublishPrompt
          postTopic={lastPublishedTopic}
          priorDismissCount={
            user?.userPreferences?.planCPromptDismissCount ?? 0
          }
          contextFilled={Boolean(user?.userProfile?.professionalContext)}
          onDismiss={handlePromptDismiss}
          onCtaClick={handlePromptCta}
        />
      )}
      {openTransitionAlert && (
        <TransitionAlert
          severity="error"
          className="TRANSITION__ALERT__BLOCK"
          open={openTransitionAlert}
          handleOpenTransitionAlert={handleCloseTransitionAlert}
          message={DRAFT.TRANSITION_ALERT.COOLDOWN_PERIOD_ON}
        />
      )}
      {!authPath ? (
        <div className="APP__CONTAINER__WRAPPER">
          {!isInterestsRoute && (
            <header>
              <div className="VERTICAL__HEADER">
                <Header />
              </div>
            </header>
          )}
          <main
            className={`CONTENT__COLUMN${isFullWidthRoute ? ' is-profile-route' : ''}`}
          >
            <Routes>{allRoutes}</Routes>
          </main>
          {!isFullWidthRoute && (
            <section className="SIDE__CONTENT">
              <SearchBar />
              {isOnFeedRoute && user && (
                <ProfileCompletionNudge
                  surface="feed_sidebar"
                  shouldShow={planC.shouldShow}
                  completionPct={planC.completionPct}
                  dismissCount={
                    user?.userPreferences?.planCNudgeDismissCount ?? 0
                  }
                  context={user?.userProfile?.professionalContext ?? null}
                  onDismiss={planC.recordDismiss}
                  onCtaClick={() =>
                    navigate(
                      `/user/${user?.username}/profile#professional-background`,
                    )
                  }
                />
              )}
              {sectionOrder.map((section, idx) => {
                const pos = idx === 0 ? 'top' : 'bottom';
                if (section === 'trending') {
                  return (
                    <TrendingSection
                      key="trending"
                      position={pos as 'top' | 'bottom'}
                      onMove={handleSwapSections}
                    />
                  );
                }
                return (
                  <SharedWithYou
                    key="shared"
                    position={pos as 'top' | 'bottom'}
                    onMove={handleSwapSections}
                  />
                );
              })}
            </section>
          )}
        </div>
      ) : (
        <div className="APP__WRAPPER-NO-TOP-MARGIN">
          <main>
            <Routes>{allRoutes}</Routes>
          </main>
        </div>
      )}
      {location.pathname === LOGOUT && (
        <Footer footerText="this is the footer" />
      )}
    </div>
  );
}
